/**
 * Core Layout Types
 *
 * These types define the data model for the 3D auto layout system.
 * CRITICAL: This file has ZERO Babylon.js imports. Layout is pure data.
 */

// === PRIMITIVE TYPES ===

/** Sizing mode for a dimension */
export type SizeMode = 'Fixed' | 'Hug' | 'Fill';

/** Position mode for children within a container.
 *  'Auto' = participates in flex flow (default).
 *  'Absolute' = removed from flow. The child keeps its authored position;
 *  the engine still resolves its size (Fill/percent measure against the
 *  parent's inner size). */
export type PositionMode = 'Auto' | 'Absolute';

/** Axis in 3D space */
export type LayoutAxis = 'X' | 'Y' | 'Z';

/**
 * Shared numeric tolerance for layout arithmetic (meters). Used for flex
 * freeze/termination thresholds, bake change detection, and legacy-scale
 * matching — one named constant instead of scattered inline 1e-6s.
 */
export const LAYOUT_EPSILON = 1e-6;

/** Alignment for cross axes */
export type LayoutAlignment = 'Start' | 'Center' | 'End' | 'Stretch';

/** Alignment for main axis (includes distribution options) */
export type MainAlignment = 'Start' | 'Center' | 'End' | 'SpaceBetween' | 'SpaceAround' | 'SpaceEvenly';

/** 3D vector in layout units */
export interface Vector3D {
  x: number;
  y: number;
  z: number;
}

// === LAYOUT BOX ===

/**
 * LayoutBox - Defines the size and sizing rules for a node
 */
export interface LayoutBox {
  /** Target size in layout units (before min/max clamping) */
  size: Vector3D;

  /** How each axis determines its size */
  sizeMode: {
    x: SizeMode;
    y: SizeMode;
    z: SizeMode;
  };

  /** Minimum size constraints */
  min: Vector3D;

  /** Maximum size constraints */
  max: Vector3D;

  /**
   * Tracks whether min was explicitly set by the author (e.g., min-w-0, min-h-0 in Tailwind).
   * When false/undefined, flex Fill items use CSS `min-height: auto` behavior (content-based minimum).
   * When true, the explicit min value is used even if it's smaller than content.
   */
  minExplicitX?: boolean;
  minExplicitY?: boolean;
  minExplicitZ?: boolean;

  /**
   * This axis's `size` is an authored flex-basis, not a measurement — so it
   * is not a `min-height:auto` content floor. Set by the data edge on Fill
   * axes; a caller that puts a real measured size on the box leaves it unset.
   */
  sizeIsBasisX?: boolean;
  sizeIsBasisY?: boolean;
  sizeIsBasisZ?: boolean;
}

// === LAYOUT CONTAINER ===

/**
 * Padding definition for 6 sides of a 3D container
 */
export interface LayoutPadding {
  left: number;
  right: number;
  top: number;
  bottom: number;
  front: number;
  back: number;
}

/**
 * LayoutContainer - Defines auto layout rules for a container node
 */
export interface LayoutContainer {
  /** Main axis for child flow (X = horizontal, Y = vertical, Z = depth) */
  directionAxis: LayoutAxis;

  /**
   * Wrap children to the next line/layer when the main axis overflows.
   * Only wraps when explicitly true; absent/false means no wrapping
   * (children shrink per flexShrink and may overflow).
   */
  wrap?: boolean;

  /**
   * Which axis wrapped lines stack along. When it names the second cross
   * axis, the cross-axis ROLES swap for this container — lines, crossGap,
   * and alignContent follow the chosen axis; layers take the other (the
   * same role re-mapping `directionAxis` already performs). Any other
   * value (absent, the main axis, the first cross axis) yields the
   * default order. Only meaningful when `wrap` is true.
   */
  wrapAxis?: LayoutAxis;

  /** Space between children in layout units */
  gap: number;

  /** Cross-axis spacing between wrapped lines/layers; falls back to `gap` when absent */
  crossGap?: number;

  /** Internal padding on all 6 sides */
  padding: LayoutPadding;

  /** How children are distributed along main axis */
  alignMain: MainAlignment;

  /** How children are aligned on first cross axis */
  alignCrossA: LayoutAlignment;

  /** How children are aligned on second cross axis */
  alignCrossB: LayoutAlignment;

  /** How wrapped lines/layers are distributed */
  alignContentCrossA?: MainAlignment;
  alignContentCrossB?: MainAlignment;

  /** Derived by the layout engine during solve */
  resolvedWrap?: boolean;

  /**
   * Lets this container shrink below its content's measured size — CSS
   * `min-width: 0` on a flex item, which `overflow: hidden` implies.
   *
   * NOT a render effect: nothing is masked, scissored or clipped. It is read
   * in exactly one place, `computeFlexMin`, where it removes the automatic
   * content floor so a Fill container can be squeezed by its siblings. The
   * name is inherited and it reads like a visual property; it is a sizing
   * one. (Content that overflows still renders — see the media `fit` work
   * for the cases that actually want cropping.)
   */
  clipContent?: boolean;

  /**
   * Fit the container's single flow child to its width and height — the
   * `ContentSlot` behavior (LAYOUT_ENGINE.md §5.2). The engine derives a
   * uniform {@link LayoutNode.flowScale} for that child on every solve.
   * Ignored on a wrapping container, and when there is not exactly one flow
   * child.
   */
  contentFit?: { fit: 'contain' | 'scale-down' | 'none'; zooms?: number[] };
}

// === LAYOUT ITEM INPUT (authored fields) ===

/**
 * Top-left offset of an Absolute child inside its containing box, in meters.
 *
 * Corner space, the same space flow children's positions resolve in: +X right
 * and +Y DOWN from the containing box's top-left. Omitted sides are 0.
 *
 * Deliberately 2D. Depth is not a corner-space concept for an overlay — an
 * Absolute child's Z stays where its own transform puts it.
 */
export interface LayoutInset {
    /**
     * Distance from the containing box's left edge to the child's left edge.
     * @min 0
     * @step 0.01
     */
    left?: number;

    /**
     * Distance from the containing box's top edge to the child's top edge.
     * @min 0
     * @step 0.01
     */
    top?: number;
}

/**
 * Authored item fields — the user-facing data for how a child sizes inside
 * a layout container. Both LayoutData and LayoutItemData extend this.
 * JSDoc tags (@default, @step, @group) drive schema generation for the editor.
 */
export interface LayoutItemInput {
    /**
     * Explicit width in meters.
     * @default 1
     * @step 0.01
     * @min 0
     * @group Sizing
     */
    width?: number;

    /**
     * Explicit height in meters.
     * @default 1
     * @step 0.01
     * @min 0
     * @group Sizing
     */
    height?: number;

    /**
     * Explicit depth in meters.
     * @default 1
     * @step 0.01
     * @min 0
     * @group Sizing
     */
    depth?: number;

    /**
     * How width is determined.
     * Fixed = use explicit width, Hug = shrink to content, Fill = expand to parent.
     * @default "Fixed"
     * @group Sizing
     */
    widthMode?: SizeMode;

    /**
     * How height is determined.
     * @default "Fixed"
     * @group Sizing
     */
    heightMode?: SizeMode;

    /**
     * How depth is determined.
     * @default "Fixed"
     * @group Sizing
     */
    depthMode?: SizeMode;

    /**
     * Minimum width in meters — a floor for Fill/shrink resolution.
     * Absent means unconstrained.
     * @min 0
     * @step 0.01
     * @group Sizing
     */
    minWidth?: number;

    /**
     * Maximum width in meters — a size ceiling, including wrapping for Hug Text.
     * Absent means unconstrained.
     * @min 0
     * @step 0.01
     * @group Sizing
     */
    maxWidth?: number;

    /**
     * Minimum height in meters — a floor for Fill/shrink resolution.
     * Absent means unconstrained.
     * @min 0
     * @step 0.01
     * @group Sizing
     */
    minHeight?: number;

    /**
     * Maximum height in meters — a ceiling for Fill/grow resolution.
     * Absent means unconstrained.
     * @min 0
     * @step 0.01
     * @group Sizing
     */
    maxHeight?: number;

    /**
     * Minimum depth in meters — a floor for Fill/shrink resolution.
     * Absent means unconstrained.
     * @min 0
     * @step 0.01
     * @group Sizing
     */
    minDepth?: number;

    /**
     * Maximum depth in meters — a ceiling for Fill/grow resolution.
     * Absent means unconstrained.
     * @min 0
     * @step 0.01
     * @group Sizing
     */
    maxDepth?: number;

    /**
     * Growth weight along the main axis when the container has free space.
     * 0 = auto: Fill children grow with weight 1, Fixed/Hug children don't.
     * Set > 0 to weight growth among siblings (like CSS flex-grow).
     * @default 0
     * @step 0.1
     * @min 0
     * @group Flex
     */
    flexGrow?: number;

    /**
     * Flex shrink factor.
     * @default 1
     * @step 0.1
     * @min 0
     * @group Flex
     */
    flexShrink?: number;

    /**
     * Position mode. Auto = arranged by the layout flow.
     * Absolute = removed from flow: the object keeps its own transform
     * position, but Fill/percent sizing still resolves against the parent.
     * @default "Auto"
     * @group Position
     */
    positionMode?: PositionMode;

    /**
     * Top-left offset for an Absolute child, in meters, from the containing
     * box's corner to this child's own corner — CSS `left`/`top`. Read only
     * when `positionMode` is Absolute; ignored on a flow child, which takes
     * its position from the flow.
     *
     * It exists because a corner is the only placement an author can state
     * ahead of the solve. The transform an Absolute child otherwise keeps is
     * CENTRE-based, and converting corner to centre needs the containing size
     * AND the child's own size — neither of which exists until layout runs,
     * so anything upstream that converts early has to guess both and is wrong
     * by half the difference whenever either extent is Hug or Fill. The
     * solver holds both numbers at the moment it places the child.
     *
     * @group Position
     */
    inset?: LayoutInset;

    /**
     * This item's own alignment on the container's FIRST cross axis,
     * overriding the container's `alignCross` for this child alone — CSS
     * `align-self`. Stretch fills the cross extent.
     *
     * Unset inherits from the container, which is not the same statement as
     * setting the container's value explicitly: an inherited value follows the
     * container when it changes, an authored one does not.
     * @group Alignment
     */
    alignSelfCross?: LayoutAlignment;

    /**
     * This item's own alignment on the container's SECOND cross axis,
     * overriding `alignCrossB` for this child alone. In a vertical container
     * that is the depth axis, which is how a single card sits proud of the
     * stack it flows in.
     *
     * Unset inherits from the container.
     * @group Alignment
     */
    alignSelfCrossB?: LayoutAlignment;
}

// === LAYOUT ITEM (engine-internal, extends authored input) ===

/**
 * LayoutItem - Full engine representation. Extends LayoutItemInput with
 * engine-derived and advanced fields not exposed in the default editor UI.
 */
export interface LayoutItem extends LayoutItemInput {
  /** How this child sizes along parent's main axis (derived by engine pre-pass) */
  mainAxisMode: SizeMode;

  /** Percentage width/height against parent */
  widthPercent?: number;
  heightPercent?: number;

  /** Flex basis override (null = use size) */
  flexBasis?: number | null;

  /** Optional ordering independent of tree order (like CSS order) */
  order?: number;

}

// === LAYOUT NODE ===

/**
 * LayoutNode - Core layout data structure
 *
 * CRITICAL: This is pure layout data. No Babylon types, no rendering concerns.
 */
export interface LayoutNode {
  /** Unique identifier */
  id: string;

  /** Parent node ID (null for root) */
  parentId: string | null;

  /** Child node IDs in order */
  childrenIds: string[];

  /** Position relative to parent in layout units */
  position: Vector3D;

  /** Rotation in radians (Euler angles) */
  rotation: Vector3D;

  /** Size and sizing rules */
  layoutBox: LayoutBox;

  /** Behavior inside parent container (null if not in a container) */
  layoutItem: LayoutItem | null;

  /** Container rules (null if not a container) */
  layoutContainer: LayoutContainer | null;

  /** Final resolved size after layout computation */
  resolvedSize: Vector3D;

  /** Final resolved position in parent space */
  resolvedPosition: Vector3D;

  /**
   * The uniform scale a `contentFit` parent gives this node, derived by the
   * engine on each solve. The node's own box (`resolvedSize`) and its
   * children stay at scale 1; the parent's flow sees the box times this.
   * Undefined when no fit applies.
   *
   * Two roles, one field. Before a solve, a `contentFit` parent's child carries
   * the placeholder `1`: only "a fit applies here" is meant by it, and the
   * measure reads the node at scale 1. The solve OVERWRITES it with the derived
   * scale (never multiplies it), so a value of `1` before the solve says
   * nothing about the fit and nothing should read it as one.
   */
  flowScale?: number;
}

// === FACTORY FUNCTIONS ===

/** Create a default Vector3D */
export function createVector3D(x = 0, y = 0, z = 0): Vector3D {
  return { x, y, z };
}

/** Create a default LayoutBox */
export function createLayoutBox(): LayoutBox {
  return {
    size: createVector3D(1, 1, 1),
    sizeMode: { x: 'Hug', y: 'Hug', z: 'Hug' },
    min: createVector3D(0, 0, 0),
    max: createVector3D(Infinity, Infinity, Infinity)
  };
}

/** Create a default LayoutPadding */
export function createPadding(value = 0): LayoutPadding {
  return {
    left: value,
    right: value,
    top: value,
    bottom: value,
    front: value,
    back: value
  };
}

/** Create a default LayoutContainer. Values are neutral zeros — behavior
 *  data (with its own @default tags) overrides these immediately. */
export function createLayoutContainer(): LayoutContainer {
  return {
    directionAxis: 'Y',
    gap: 0,
    padding: createPadding(0),
    alignMain: 'Start',
    alignCrossA: 'Start',
    alignCrossB: 'Center',
    alignContentCrossA: 'Start',
    alignContentCrossB: 'Start',
  };
}

/** Create a default LayoutItem */
export function createLayoutItem(): LayoutItem {
  return {
    mainAxisMode: 'Hug',
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: null,
    order: 0,
    alignSelfCross: undefined,
    alignSelfCrossB: undefined,
  };
}

/** Generate a unique ID */
export function generateId(): string {
  return `node_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/** Create a new LayoutNode with defaults */
export function createLayoutNode(id?: string): LayoutNode {
  return {
    id: id ?? generateId(),
    parentId: null,
    childrenIds: [],
    position: createVector3D(),
    rotation: createVector3D(),
    layoutBox: createLayoutBox(),
    layoutItem: null,
    layoutContainer: null,
    resolvedSize: createVector3D(),
    resolvedPosition: createVector3D(),
  };
}
